// A picture of the product for the front page (SITE_HOME.md, B-305): a family
// league's week, drawn with the site's own pieces — the matchup card, the
// standings' rows, the chat's lines — so it always looks like the app does
// today, in whatever look the visitor's device gives it, and costs nothing to
// serve. Made-up people, and labeled an example. One picture to a screen
// reader, with nothing inside it to press.

import type { MatchupSummary, TeamRef } from '@fsp/domain';
import { TeamMark } from '../kit';
import { Chip } from '../kit/ui';
import { MatchupCard } from './shared/MatchupCard';
import '../styles/screens/league-screens.css';

function team(
  id: string,
  name: string,
  abbreviation: string,
  ownerName: string,
  primary: string,
  secondary: string,
  wins: number,
  losses: number,
  isMine = false,
): TeamRef {
  return {
    id,
    name,
    abbreviation,
    ownerName,
    colors: { primary, secondary },
    badge: null,
    banner: null,
    record: { wins, losses, ties: 0 },
    isMine,
    titles: null,
  };
}

const LILY = team('lily', "Lily's Lightning", 'LIL', 'Lily', '#7c3aed', '#facc15', 5, 1, true);
const GRANDMA = team('grandma', "Grandma's Gridiron", 'GMA', 'Grandma', '#be185d', '#fbcfe8', 4, 2);
const DAD = team('dad', "Dad's Dynasty", 'DAD', 'Dad', '#1d4ed8', '#e5e7eb', 4, 2);

const GAME: MatchupSummary = {
  id: 'example',
  period: 6,
  status: 'final',
  home: { team: LILY, score: 104.2, projected: 104.2, yetToPlay: 0 },
  away: { team: DAD, score: 97.6, projected: 97.6, yetToPlay: 0 },
  winProbabilityHome: 1,
  liveLabel: null,
  kind: 'regular',
  rivalry: null,
  winnerTeamId: LILY.id,
  secondGame: false,
};

const TABLE = [LILY, GRANDMA, DAD];

const CHAT = [
  { who: 'Lily', text: 'I beat Dad again!! 🏆', reactions: ['😂 3', '🔥 2'] },
  { who: 'Grandma', text: 'Way to go, Lily!', reactions: ['👍 4'] },
];

export function WelcomePicture({ full }: { full: boolean }) {
  return (
    <div
      className="fsp-welcome-picture"
      role="img"
      aria-label={`An example family league: in week 6 Lily's Lightning beat Dad's Dynasty 104.2 to 97.6,${full ? ' Lily leads the standings,' : ''} and Grandma cheers her on in the league chat.`}
    >
      <div className="fsp-surface fsp-welcome-frame">
        <div className="fsp-welcome-frame-head">
          <span className="fsp-welcome-frame-league">Kitchen Table League</span>
          <Chip>Example</Chip>
        </div>
        <div className="fsp-cap">Week 6</div>
        <MatchupCard m={GAME} compact link={false} />
        {/* A phone has room for the game and the chat; the standings join
            them from a tablet up. */}
        {full ? (
          <>
            <div className="fsp-cap">Standings</div>
            <div>
              {TABLE.map((t, i) => (
                <div
                  key={t.id}
                  className={`fsp-row fsp-standings-cols fsp-standings-phone-simple ${t.isMine ? 'fsp-row-selected' : ''}`}
                >
                  <span className="fsp-num fsp-muted">{i + 1}</span>
                  <TeamMark team={t} size={24} />
                  <div className="fsp-standings-who">
                    <div className="fsp-name">
                      <span className="fsp-name-text">{t.name}</span>
                    </div>
                    <div className="fsp-sub">{t.ownerName}</div>
                  </div>
                  <span className="fsp-num fsp-right">
                    {t.record.wins}–{t.record.losses}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : null}
        <div className="fsp-cap">League chat</div>
        <div>
          {CHAT.map((c) => (
            <div key={c.who} className="fsp-chat-msg">
              {/* The name in bold, not its team's color: a team's purple
                  on the dark ground read under 4.5:1 (UX-32). */}
              <span className="fsp-chat-author">{c.who}</span> <span>{c.text}</span>
              <div className="fsp-chat-reactions">
                {c.reactions.map((r) => (
                  <span key={r} className="fsp-chat-reaction">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
